import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Lightning, Users, Sparkle, GameController } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GAMES = ['Catan', 'Chess', 'Magic: The Gathering', 'Pokemon', 'Yu-Gi-Oh!'];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function GameNightPlannerPage() {
  const navigate = useNavigate();
  const [selectedGame, setSelectedGame] = useState('Catan');
  const [playerCount, setPlayerCount] = useState(4);
  const [preferredDays, setPreferredDays] = useState(['saturday', 'sunday']);
  const [plan, setPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/planner/plans`, { withCredentials: true });
      setPlans(data);
    } catch (err) { /* silent */ }
  };

  const generatePlan = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/planner/suggest`, {
        game_name: selectedGame,
        player_count: playerCount,
        preferred_days: preferredDays
      }, { withCredentials: true });
      setPlan(data);
      toast.success('Game night planned!');
      fetchPlans();
    } catch (err) {
      toast.error('Failed to generate plan');
    } finally { setLoading(false); }
  };

  const toggleDay = (day) => {
    setPreferredDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  return (
    <div className="app-container" data-testid="game-night-planner-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Sparkle size={24} weight="duotone" className="text-[#FACC15]" />
              Game Night Planner
            </h1>
            <p className="text-[#71717A] text-xs">AI-powered game night suggestions</p>
          </div>
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {/* Game Selection */}
        <div>
          <label className="text-[#A1A1AA] text-xs mb-2 block">Game</label>
          <div className="horizontal-scroll">
            {GAMES.map(g => <GamePill key={g} game={g} selected={selectedGame === g} onClick={() => setSelectedGame(g)} />)}
          </div>
        </div>

        {/* Player Count */}
        <div>
          <label className="text-[#A1A1AA] text-xs mb-2 block">Players Needed</label>
          <div className="flex gap-2">
            {[2, 3, 4, 5, 6].map(n => (
              <button key={n} onClick={() => setPlayerCount(n)}
                className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${playerCount === n ? 'bg-[#9D4CDD] text-white' : 'bg-[#181824] text-[#71717A]'}`}>
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Preferred Days */}
        <div>
          <label className="text-[#A1A1AA] text-xs mb-2 block">Preferred Days</label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map(d => (
              <button key={d} onClick={() => toggleDay(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  preferredDays.includes(d) ? 'bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/30' : 'bg-[#181824] text-[#71717A] border border-transparent'
                }`}>
                {d.charAt(0).toUpperCase() + d.slice(1, 3)}
              </button>
            ))}
          </div>
        </div>

        {/* Generate Button */}
        <button onClick={generatePlan} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="generate-plan-btn">
          <Lightning size={18} weight="fill" />
          {loading ? 'Planning...' : 'Plan My Game Night'}
        </button>

        {/* Generated Plan */}
        {plan && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-gradient-to-br from-[#9D4CDD]/10 to-[#00F0FF]/5 border border-[#9D4CDD]/20">
            <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
              <Calendar size={18} className="text-[#FACC15]" />
              {plan.game_name} Night - {plan.suggested_day?.charAt(0).toUpperCase() + plan.suggested_day?.slice(1)}
            </h3>

            {plan.ai_suggestion && (
              <p className="text-[#A1A1AA] text-xs leading-relaxed mb-3">{plan.ai_suggestion}</p>
            )}

            <div className="space-y-2">
              <p className="text-[#71717A] text-[10px] font-medium">Suggested Players:</p>
              {plan.suggested_players?.map((p, i) => (
                <div key={p.user_id} className="flex items-center gap-2 p-2 rounded-lg bg-[#181824]">
                  <div className="w-6 h-6 rounded-full bg-[#9D4CDD]/20 flex items-center justify-center text-[10px] text-[#9D4CDD] font-bold">{i + 1}</div>
                  <div className="flex-1">
                    <p className="text-white text-xs">{p.name}</p>
                    <p className="text-[#71717A] text-[10px]">Lv.{p.level} • {p.play_style || 'casual'}</p>
                  </div>
                  <span className="text-[#00F0FF] text-[10px] font-bold">{p.percentage}%</span>
                </div>
              ))}
            </div>

            <button onClick={() => navigate('/create-event')} className="mt-3 w-full py-2 rounded-lg bg-[#39FF14]/10 text-[#39FF14] text-xs font-medium border border-[#39FF14]/20">
              Create This Session
            </button>
          </motion.div>
        )}

        {/* Past Plans */}
        {plans.length > 0 && (
          <div>
            <h3 className="text-[#A1A1AA] text-xs font-medium mb-2">Previous Plans</h3>
            {plans.slice(0, 3).map((p, i) => (
              <div key={i} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 mb-2">
                <div className="flex justify-between">
                  <span className="text-white text-xs font-medium">{p.game_name} - {p.suggested_day}</span>
                  <span className="text-[#71717A] text-[10px]">{p.total_players} players</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
